/**
 * @param {object} member
 * @param {string} sortBy
 * @returns {string|number|boolean|null}
 */
export function getSortValue(member, sortBy) {
    if (!member) return null;

    switch (sortBy) {
        case 'member_id':
            return member.member_id ?? 0;
        case 'name':
            return (member.name || '').toLowerCase();
        case 'address':
            return (member.address || '').toLowerCase();
        case 'created_at':
            return member.created_at ? new Date(member.created_at).getTime() : 0;
        case 'subscription_start': {
            const d = member.subscription?.renewed_date || member.subscription?.start_date;
            return d ? new Date(d).getTime() : 0;
        }
        case 'subscription_end':
            return member.subscription?.end_date
                ? new Date(member.subscription.end_date).getTime()
                : 0;
        case 'phone':
            return (member.phone || '').toLowerCase();
        case 'location_name':
            return (member.location_id?.name || '').toLowerCase();
        case 'is_active':
            return member.is_active ? 1 : 0;
        case 'trainer_name':
            return (member.trainer_id?.name || '').toLowerCase();
        case 'trainer_phone':
            return (member.trainer_id?.phone_number || '').toLowerCase();
        case 'is_trainer':
            return member.is_trainer && member.trainer_id ? 1 : 0;
        default:
            return null;
    }
}

function compareValues(a, b, sortDir) {
    const aEmpty = a === null || a === undefined || a === '';
    const bEmpty = b === null || b === undefined || b === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    if (typeof a === 'number' && typeof b === 'number') {
        return sortDir === 'asc' ? a - b : b - a;
    }
    if (typeof a === 'boolean' && typeof b === 'boolean') {
        const an = a ? 1 : 0;
        const bn = b ? 1 : 0;
        return sortDir === 'asc' ? an - bn : bn - an;
    }

    const sa = String(a);
    const sb = String(b);
    const cmp = sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? cmp : -cmp;
}

/**
 * @param {object[]} members
 * @param {string|null} sortBy
 * @param {'asc'|'desc'|null} sortDir
 * @returns {object[]}
 */
export function sortMembers(members, sortBy, sortDir) {
    if (!sortBy || !sortDir || !Array.isArray(members)) {
        return members ? [...members] : [];
    }
    const indexed = members.map((m, i) => ({ m, i }));
    indexed.sort((a, b) => {
        const va = getSortValue(a.m, sortBy);
        const vb = getSortValue(b.m, sortBy);
        const c = compareValues(va, vb, sortDir);
        return c !== 0 ? c : a.i - b.i;
    });
    return indexed.map((x) => x.m);
}

/**
 * Tri-state: null -> asc -> desc -> null
 * @param {string|null} currentSortBy
 * @param {'asc'|'desc'|null} currentSortDir
 * @param {string} columnKey
 * @returns {{ sortBy: string|null, sortDir: 'asc'|'desc'|null }}
 */
export function nextSortState(currentSortBy, currentSortDir, columnKey) {
    if (currentSortBy !== columnKey) {
        return { sortBy: columnKey, sortDir: 'asc' };
    }
    if (currentSortDir === 'asc') {
        return { sortBy: columnKey, sortDir: 'desc' };
    }
    return { sortBy: null, sortDir: null };
}

export function formatTrainerId(trainerRef) {
    if (!trainerRef?._id) return '—';
    const id = String(trainerRef._id);
    return id.length > 8 ? id.slice(-8).toUpperCase() : id.toUpperCase();
}
